package dev.youndie.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import dev.youndie.site.components.layouts.PageLayoutData
import dev.youndie.site.components.widgets.Chip
import dev.youndie.site.components.widgets.LibraryCard
import dev.youndie.site.components.widgets.Prompt
import dev.youndie.site.components.widgets.SectionHead
import dev.youndie.site.model.AUTHOR_NAME
import dev.youndie.site.model.BLOG_VISIBLE
import dev.youndie.site.model.EMAIL
import dev.youndie.site.model.GITHUB
import dev.youndie.site.model.LIBRARIES
import dev.youndie.site.model.MAVEN_REPO
import dev.youndie.site.model.MEDIUM
import dev.youndie.site.model.personJsonLd
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val HOME_DESCRIPTION =
    "Open-source Kotlin libraries by Pavel Votyakov: an S3 client and a MongoDB driver for " +
        "Kotlin/Native, a self-hosted crash tracker, screenshot testing for Compose Multiplatform, " +
        "a saga engine and a message broker for the JVM."

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            // Not "kotlin.website". A domain in a search result says nothing anybody
            // searched for, and competing on the word "kotlin" against JetBrains is not
            // a competition — what brings people here is the specific thing each library
            // does, so the title names those instead.
            title = "$AUTHOR_NAME — Kotlin/Native and JVM open-source libraries",
            socialTitle = "$AUTHOR_NAME — Kotlin developer",
            description = HOME_DESCRIPTION,
            jsonLd = personJsonLd(
                description = HOME_DESCRIPTION,
                sameAs = listOf(GITHUB, MEDIUM),
            ),
        )
    )
}

@Page
@Composable
fun HomePage() {
    Section(attrs = { classes("hero") }) {
        Prompt("whoami")
        H1 {
            Text("pavel votyakov")
            Span(attrs = { classes("cursor") }) {}
        }
        Div(attrs = { classes("role") }) { Text("kotlin developer · 14 years of mobile and backend") }

        P {
            Text("Most of what's here is ")
            Span(attrs = { classes("hl") }) { Text("infrastructure I needed and couldn't find in the right size") }
            Text(
                " — a crash tracker that fits on one box, logs a coding agent queries over MCP instead of " +
                    "being handed a dashboard link, a saga engine that survives the process dying halfway " +
                    "through."
            )
        }
        P {
            Text(
                "Kotlin as far as it will go: Ktor services on the JVM, native Linux binaries with nothing " +
                    "to install underneath them, Compose Multiplatform on the desktop, KMP libraries " +
                    "published for all three. Several of them run on my own cluster, and where something " +
                    "claims a number the methodology is in the repository next to it."
            )
        }
        P {
            Text("Day job is leading Android on a Kotlin Multiplatform banking app.")
        }

        Div(attrs = { classes("hero-links") }) {
            Chip(GITHUB, "github/youndie")
            Chip("mailto:$EMAIL", EMAIL)
            Chip("/about", "about")
            if (BLOG_VISIBLE) Chip("/blog", "blog")
        }
    }

    Section(attrs = { classes("section") }) {
        SectionHead("libraries", "${LIBRARIES.size} repos")
        P(attrs = { classes("section-note") }) {
            Text("Most of them publish to my own Reposilite at ")
            A(href = MAVEN_REPO, attrs = {
                target(ATarget.Blank)
                attr("rel", "noopener")
            }) { Text("reposilite.kotlin.website") }
            Text("; tracy, katcher and metrik also ship container images on ghcr. Everything is MIT.")
        }
        Div(attrs = { classes("lib-grid") }) {
            LIBRARIES.forEach { LibraryCard(it) }
        }
    }
}
